import {
  Autocomplete,
  Checkbox,
  CheckBoxIcon,
  CheckBoxOutlineBlankIcon,
  TextField,
} from '@razz-team/ui-web';
import React, { useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  EventsValue,
  eventsValueToOptions,
  eventsValueToValue,
  getOptionLabel,
  valueToEventsValue,
} from './utils';
import { WebhookEventChip } from './WebhookEventChip';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export function WebhookEventsField({
  name,
  label,
  initialValue,
}: {
  name: string;
  label: React.ReactNode;
  initialValue: EventsValue;
}) {
  const { t } = useTranslation();
  const { field } = useController({ name });

  const handleChange = useCallback(
    (_: unknown, value: string[]) => {
      field.onChange(valueToEventsValue(value));
    },
    [field],
  );

  const value = useMemo(() => eventsValueToValue(field.value), [field.value]);
  const options = useMemo(() => eventsValueToOptions(initialValue), [initialValue]);

  const handleDelete = useCallback(
    (event: string) => {
      field.onChange(valueToEventsValue(value.filter((i) => i !== event)));
    },
    [field, value],
  );

  return (
    <>
      <Autocomplete
        multiple
        value={value}
        options={options}
        disabled={field.disabled}
        onBlur={field.onBlur}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        slotProps={{
          chip: {
            color: 'primary',
            size: 'small',
          },
        }}
        disableCloseOnSelect
        renderInput={(params) => <TextField {...params} label={label} />}
        renderValue={(value) => t(`{{count}} events`, { count: value.length })}
        renderOption={({ key, ...props }, option, { selected }) => (
          <li key={key} {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {getOptionLabel(option)}
          </li>
        )}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {value.map((item) => (
          <WebhookEventChip key={item} event={item} onDelete={handleDelete} />
        ))}
      </div>
    </>
  );
}
